// IndexedDB outbox for prescription submissions.
//
// Guarantees & behaviour:
//  - **Exactly-once**: the client-generated UUID is used as the Rx primary key.
//    Reconciliation `SELECT` runs before every INSERT so a row that landed but
//    whose response never returned (partial-success) is detected and dequeued
//    without a second insert attempt. Duplicate-key errors are also treated as
//    "already synced" for defense-in-depth.
//  - **Retry with exponential backoff**: transient failures (network, 5xx,
//    JWT-expired, timeouts) are retried with a 5s → 15s → 60s → 5m → 30m → 2h
//    schedule, capped. Items whose next_retry_at is in the future are skipped
//    unless the user forces a retry.
//  - **Permanent errors are quarantined**: RLS violations, foreign-key
//    failures, invalid-JSON, NOT NULL violations, and 400 responses stop
//    retrying and set `permanent: true` so the UI can prompt the doctor to
//    edit or discard.

import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { supabase } from "@/integrations/supabase/client";
import type { Medication } from "@/types";
import { trackSync } from "@/lib/sync-audit";

const DB_NAME = "medidesk-outbox";
const DB_VERSION = 2; // bumped for schema additions (backoff/permanent fields)
const STORE = "pending_prescriptions";

export interface PendingPrescription {
  id: string;                 // client uuid — also used as the Rx primary key
  user_id: string;
  patient_id: string;
  diagnosis: string;
  medications: Medication[];
  notes: string;
  date: string;               // ISO YYYY-MM-DD
  extra: Record<string, any>;
  queued_at: number;          // Date.now() when first enqueued
  attempts: number;           // total send attempts (transient failures only)
  last_error?: string;        // last error message, if any
  last_error_code?: string;   // Postgres/PostgREST code when known
  first_error_at?: number;    // Date.now() of first failure
  next_retry_at?: number;     // Date.now() before which we won't retry
  permanent?: boolean;        // if true, do NOT auto-retry — needs user action
}

interface OutboxSchema extends DBSchema {
  pending_prescriptions: {
    key: string;
    value: PendingPrescription;
    indexes: { by_queued_at: number };
  };
}

let dbPromise: Promise<IDBPDatabase<OutboxSchema>> | null = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<OutboxSchema>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: "id" });
          store.createIndex("by_queued_at", "queued_at");
        }
        // v2: no store-shape change; the new fields are optional so existing
        // records upgrade transparently.
        void oldVersion;
      },
    });
  }
  return dbPromise;
}

// --- Change notification (in-tab). BroadcastChannel keeps multi-tab counts in sync. ---
type Listener = (count: number) => void;
const listeners = new Set<Listener>();
const channel =
  typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("medidesk-outbox") : null;

async function emitCount() {
  const c = await countPending();
  listeners.forEach((l) => l(c));
  channel?.postMessage({ type: "count", count: c });
}

channel?.addEventListener("message", (e) => {
  if (e.data?.type === "count") listeners.forEach((l) => l(e.data.count));
});

export function subscribeOutbox(fn: Listener): () => void {
  listeners.add(fn);
  countPending().then(fn);
  return () => {
    listeners.delete(fn);
  };
}

// --- Outbox lifecycle events (retry / permanent failure / synced) ---
// Distinct from the count listener so callers (e.g. desktop notifier) only
// wake on state transitions, not every count refresh.
export type OutboxEvent =
  | { type: "retry"; item: PendingPrescription; error: string; attempts: number; next_retry_at?: number }
  | { type: "failure"; item: PendingPrescription; error: string; code?: string }
  | { type: "synced"; count: number };

type EventListener = (e: OutboxEvent) => void;
const eventListeners = new Set<EventListener>();

function emitEvent(e: OutboxEvent) {
  eventListeners.forEach((l) => {
    try { l(e); } catch (err) { console.error("outbox event listener failed:", err); }
  });
}

export function subscribeOutboxEvents(fn: EventListener): () => void {
  eventListeners.add(fn);
  return () => { eventListeners.delete(fn); };
}

// --- CRUD ---
export async function enqueuePrescription(
  rx: Omit<PendingPrescription, "queued_at" | "attempts">,
): Promise<PendingPrescription> {
  const record: PendingPrescription = { ...rx, queued_at: Date.now(), attempts: 0 };
  const db = await getDb();
  await db.put(STORE, record);
  trackSync({ source: "prescription", ref: record.id, status: "queued", table: "prescriptions", op: "insert", label: "Prescription" });
  emitCount();
  void requestBackgroundSync();
  return record;
}

export async function requestBackgroundSync(): Promise<boolean> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sync = (reg as any).sync;
    if (!sync || typeof sync.register !== "function") return false;
    await sync.register("outbox-drain");
    return true;
  } catch {
    return false;
  }
}

export async function listPending(): Promise<PendingPrescription[]> {
  const db = await getDb();
  return db.getAllFromIndex(STORE, "by_queued_at");
}

export async function countPending(): Promise<number> {
  const db = await getDb();
  return db.count(STORE);
}

export async function removePending(id: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE, id);
  emitCount();
}

/** Reset backoff and permanent flag so an item is picked up on the next drain. */
export async function resetPendingRetry(id: string): Promise<void> {
  const db = await getDb();
  const item = await db.get(STORE, id);
  if (!item) return;
  item.next_retry_at = undefined;
  item.permanent = false;
  item.last_error = undefined;
  item.last_error_code = undefined;
  await db.put(STORE, item);
  emitCount();
}

// --- Error classification & backoff ---

// Postgres SQLSTATE codes and PostgREST hints that are NOT retryable.
// See: https://www.postgresql.org/docs/current/errcodes-appendix.html
const PERMANENT_PG_CODES = new Set([
  "23502", // not_null_violation
  "23503", // foreign_key_violation
  "23514", // check_violation
  "22P02", // invalid_text_representation (bad uuid, enum, etc.)
  "42501", // insufficient_privilege (RLS)
  "42703", // undefined_column
  "42P01", // undefined_table
  "PGRST301", // PostgREST: JWT expired -> actually transient, handled below
]);

function isPermanentError(err: any): boolean {
  const code: string | undefined = err?.code;
  if (code === "PGRST301") return false; // JWT expired: transient after refresh
  if (code && PERMANENT_PG_CODES.has(code)) return true;
  const msg: string = String(err?.message ?? "").toLowerCase();
  if (/row-level security|permission denied/.test(msg)) return true;
  if (/violates foreign key|not-?null constraint/.test(msg)) return true;
  if (/invalid input syntax|invalid uuid/.test(msg)) return true;
  return false;
}

function isDuplicate(err: any): boolean {
  if (err?.code === "23505") return true;
  return /duplicate key|already exists/i.test(String(err?.message ?? ""));
}

/** Exponential backoff schedule in ms, capped at 2 hours. */
const BACKOFF_MS = [
  5_000,        // 5s
  15_000,       // 15s
  60_000,       // 1m
  5 * 60_000,   // 5m
  30 * 60_000,  // 30m
  2 * 60 * 60_000, // 2h
];

function nextBackoff(attempts: number): number {
  const i = Math.min(attempts, BACKOFF_MS.length - 1);
  // Small jitter (±20%) to avoid thundering herds when many items retry together.
  const base = BACKOFF_MS[i];
  const jitter = base * 0.2 * (Math.random() * 2 - 1);
  return Math.max(1_000, Math.round(base + jitter));
}

async function markTransientFailure(item: PendingPrescription, err: any) {
  const db = await getDb();
  const existing = await db.get(STORE, item.id);
  if (!existing) return;
  existing.attempts += 1;
  existing.last_error = err?.message ?? String(err);
  existing.last_error_code = err?.code;
  existing.first_error_at ??= Date.now();
  existing.next_retry_at = Date.now() + nextBackoff(existing.attempts);
  existing.permanent = false;
  await db.put(STORE, existing);
  emitCount();
  emitEvent({
    type: "retry",
    item: existing,
    error: existing.last_error ?? "unknown",
    attempts: existing.attempts,
    next_retry_at: existing.next_retry_at,
  });
}

async function markPermanentFailure(item: PendingPrescription, err: any) {
  const db = await getDb();
  const existing = await db.get(STORE, item.id);
  if (!existing) return;
  existing.attempts += 1;
  existing.last_error = err?.message ?? String(err);
  existing.last_error_code = err?.code;
  existing.first_error_at ??= Date.now();
  existing.permanent = true;
  existing.next_retry_at = undefined;
  await db.put(STORE, existing);
  emitCount();
  emitEvent({
    type: "failure",
    item: existing,
    error: existing.last_error ?? "unknown",
    code: existing.last_error_code,
  });
}

// --- Sync ---
let syncing = false;

export interface DrainResult {
  synced: number;
  failed: number;
  reconciled: number;
  skipped: number;   // deferred by backoff or marked permanent
  permanent: number; // count of items in permanent-failure state after this run
}

/**
 * Drain pending prescriptions to Supabase.
 * @param opts.force  If true, ignore backoff timers and permanent flags.
 */
export async function drainOutbox(
  opts: { force?: boolean } = {},
): Promise<DrainResult> {
  if (syncing) return { synced: 0, failed: 0, reconciled: 0, skipped: 0, permanent: 0 };
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return { synced: 0, failed: 0, reconciled: 0, skipped: 0, permanent: 0 };
  }
  syncing = true;
  const now = Date.now();
  const result: DrainResult = { synced: 0, failed: 0, reconciled: 0, skipped: 0, permanent: 0 };

  try {
    const pending = await listPending();
    for (const item of pending) {
      if (!opts.force) {
        if (item.permanent) { result.skipped += 1; result.permanent += 1; continue; }
        if (item.next_retry_at && item.next_retry_at > now) { result.skipped += 1; continue; }
      }

      try {
        // ---- Reconciliation: has this id already landed on the server? ----
        // A previous request may have succeeded server-side but failed to
        // deliver the response (network flake). Detect that here and dequeue
        // without a second insert.
        const { data: existing, error: selErr } = await supabase
          .from("prescriptions")
          .select("id")
          .eq("id", item.id)
          .maybeSingle();

        if (selErr && !/no rows/i.test(selErr.message)) {
          throw selErr;
        }
        if (existing) {
          await removePending(item.id);
          trackSync({ source: "prescription", ref: item.id, status: "synced", table: "prescriptions", op: "insert", label: "Prescription (reconciled)", attempt: item.attempts + 1 });
          result.reconciled += 1;
          continue;
        }

        // ---- Insert with client-generated id (idempotency key) ----
        const { error } = await supabase.from("prescriptions").insert({
          id: item.id,
          user_id: item.user_id,
          patient_id: item.patient_id,
          diagnosis: item.diagnosis,
          medications: JSON.parse(JSON.stringify(item.medications)),
          notes: item.notes,
          date: item.date,
          extra: item.extra,
        });

        if (error) {
          if (isDuplicate(error)) {
            // Row landed between our SELECT and INSERT — treat as success.
            await removePending(item.id);
            trackSync({ source: "prescription", ref: item.id, status: "synced", table: "prescriptions", op: "insert", label: "Prescription (duplicate)", attempt: item.attempts + 1 });
            result.reconciled += 1;
            continue;
          }
          throw error;
        }
        await removePending(item.id);
        trackSync({ source: "prescription", ref: item.id, status: "synced", table: "prescriptions", op: "insert", label: "Prescription", attempt: item.attempts + 1 });
        result.synced += 1;
      } catch (e: any) {
        result.failed += 1;
        const emsg = String(e?.message ?? e);
        if (isPermanentError(e)) {
          await markPermanentFailure(item, e);
          trackSync({ source: "prescription", ref: item.id, status: "failed", table: "prescriptions", op: "insert", label: "Prescription", attempt: item.attempts + 1, error: emsg });
          result.permanent += 1;
        } else {
          await markTransientFailure(item, e);
          trackSync({ source: "prescription", ref: item.id, status: "retry", table: "prescriptions", op: "insert", label: "Prescription", attempt: item.attempts + 1, error: emsg });
        }
      }
    }
  } finally {
    syncing = false;
  }
  if (result.synced > 0) emitEvent({ type: "synced", count: result.synced });
  return result;
}

// --- Auto-sync scheduler ---

let retryTimer: ReturnType<typeof setTimeout> | null = null;

/** Schedule the next auto-drain at the soonest `next_retry_at` in the outbox. */
async function scheduleNextRetry(run: () => Promise<void>) {
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  const items = await listPending();
  const now = Date.now();
  const upcoming = items
    .filter((i) => !i.permanent && i.next_retry_at && i.next_retry_at > now)
    .map((i) => i.next_retry_at as number);
  if (upcoming.length === 0) return;
  const soonest = Math.min(...upcoming);
  const delay = Math.max(1_000, soonest - now);
  retryTimer = setTimeout(() => { void run(); }, delay);
}

/**
 * Attach the listeners that keep the outbox drained:
 *  - `online`               — primary iOS fallback (no Background Sync there).
 *  - `visibilitychange`     — catches tabs that were backgrounded.
 *  - SW `message`           — Background Sync woke our SW; it asks us to drain.
 *  - scheduled timer        — wakes at the next `next_retry_at` for backoff replay.
 *  - boot-time run          — handles the "reopened after long offline" case.
 */
export function installOutboxAutoSync(onResult?: (r: DrainResult) => void) {
  if (typeof window === "undefined") return () => {};
  const run = async () => {
    const r = await drainOutbox();
    if (r.synced || r.failed || r.reconciled) onResult?.(r);
    void scheduleNextRetry(run);
  };
  const onOnline = () => void run();
  const onVisible = () => {
    if (document.visibilityState === "visible") void run();
  };
  const onSwMessage = (e: MessageEvent) => {
    if (e.data?.type === "drain-outbox") void run();
  };
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", onVisible);
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", onSwMessage);
  }
  void run();
  return () => {
    window.removeEventListener("online", onOnline);
    document.removeEventListener("visibilitychange", onVisible);
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.removeEventListener("message", onSwMessage);
    }
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  };
}
